// filepath: e:\temp\update-management-app\packages\iot-management-app\src\compornents\molecules\DatapointsDrawer2.tsx
import { FC, memo, useMemo, useState, useEffect } from "react";
import { Center, Spinner, Stack, FormControl, RadioGroup, Radio, HStack, Box, Text, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText } from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from "chart.js";
import { MetricDataResult } from "../../types/api/metricDataResult";
import { useCustomMetrics } from "../../hooks/useCustomMetrics";
import { ScheduleSettings } from "../../hooks/useParameters";

// Chart.jsを使用するために必要なコンポーネントを登録
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

type Props = {
  tenant_id?: string;
  tenantJob?: ScheduleSettings;
};

export const JobMetricDrawer: FC<Props> = memo((props) => {
  const { tenant_id, tenantJob } = props;
  const { getConvertCountJobMetric, metricDataResults, loading:loadingDatapoints } = useCustomMetrics();
  // 時間間隔の状態
  const [periodset, setPeriodset] = useState<"minit" | "5minits" | "hour" | "day">("5minits");
  
  // 時間間隔変更時またはジョブ変更時にデータを取得
  useEffect(() => {
    console.log('[JobMetricDrawer] useEffect triggered', { tenant_id, tenantJob, periodset });
    if (tenant_id && tenantJob?.parampath) {
      // parampathからバッチ名を抽出 (jobs/の後の部分)
      // 例: "pref21000/jobs/pref21000-shelter-get" → "pref21000-shelter-get"
      const pathParts = tenantJob.parampath.split('/');
      const batchName = pathParts[pathParts.length - 1];
      console.log('[JobMetricDrawer] Extracted batchName from parampath:', batchName, 'parampath:', tenantJob.parampath);
      
      const endDate = new Date();
      const startDate = new Date();
      let period: number = 300;
      
      if (periodset === "minit") period = 60;
      else if (periodset === "5minits") period = 300;
      else if (periodset === "hour") period = 60 * 60;
      else if (periodset === "day") period = 60 * 60 * 24;
      
      startDate.setDate(endDate.getDate() - (period * 1440) / (60 * 60 * 24));
      
      console.log('[JobMetricDrawer] Fetching metrics', { batchName, startDate, endDate, period });
      getConvertCountJobMetric(
        tenant_id,
        batchName,
        startDate.toISOString(),
        endDate.toISOString(),
        period
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id, tenantJob?.parampath, periodset]);
  // Chart.jsのデータ形式の設定
  const chartData = useMemo(() => {
    // MetricDataResultsが存在する場合はそれを使用
    if (metricDataResults && metricDataResults.length > 0) {
      // 最初のメトリクスのタイムスタンプを基準にラベルを作成（古い日付から新しい日付の順に並べる）
      const timestamps = [...metricDataResults[0].Timestamps]; // コピーを作成
      const sortedTimestamps = timestamps.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      
      const labels = sortedTimestamps.map(timestamp => {
        const date = new Date(timestamp);
        return date.toLocaleString('ja-JP');
      });
      
      // 各MetricDataResultからデータセットを作成（Label昇順にソート）
      const datasets = metricDataResults.map((metric, index) => {
        // カラーパレットの定義（インデックスに基づいて異なる色を割り当て）
        const colors = [
          { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.5)' },
          { border: 'rgb(53, 162, 235)', background: 'rgba(53, 162, 235, 0.5)' },
          { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.5)' },
          { border: 'rgb(255, 205, 86)', background: 'rgba(255, 205, 86, 0.5)' },
          { border: 'rgb(153, 102, 255)', background: 'rgba(153, 102, 255, 0.5)' },
          { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.5)' },
          { border: 'rgb(255, 159, 64)', background: 'rgba(255, 159, 64, 0.5)' },
          { border: 'rgb(201, 203, 207)', background: 'rgba(201, 203, 207, 0.5)' }
        ];
        
        // インデックスに基づいて色を選択（循環させる）
        const colorIndex = index % colors.length;
        
        // タイムスタンプと値を対応付ける
        const sortedData = [];
        for (let i = 0; i < sortedTimestamps.length; i++) {
          // 元のデータ内で対応するタイムスタンプのインデックスを検索
          const originalIndex = metric.Timestamps.findIndex(t => t === sortedTimestamps[i]);
          if (originalIndex !== -1 && originalIndex < metric.Values.length) {
            sortedData.push(metric.Values[originalIndex]);
          } else {
            sortedData.push(null); // データがない場合はnullを挿入
          }
        }
        
        return {
          label: metric.Label || `メトリクス ${index + 1}`,
          data: sortedData,
          borderColor: colors[colorIndex].border,
          backgroundColor: colors[colorIndex].background,
        };
      });

      return {
        labels,
        datasets
      };
    }    
    // MetricDataResultsがない場合はデフォルト値を返す
    return {
      labels: [],
      datasets: []
    };
    
  }, [ metricDataResults]);    // Chart.js用のオプションを設定 - anyを使用して型エラーを回避
  const chartOptions = useMemo(() => {
    // データ種類に基づいたタイトルを設定
    const title = metricDataResults && metricDataResults.length > 0
      ? 'データポイントグラフ（メトリクス別）'
      : 'データポイントグラフ';
      
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          display: true // 凡例を常に表示
        },
        title: {
          display: true,
          text: title,
          font: {
            size: 16
          }
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
        }
      },      scales: {
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            // 目盛間隔を3倍に
            autoSkip: true,
          },
          // 左から右への時系列順に表示
          time: {
            displayFormats: {
              hour: 'HH:mm',
              day: 'MM/DD',
              week: 'MM/DD',
              month: 'YYYY/MM',
              quarter: 'YYYY [Q]Q',
              year: 'YYYY'
            }
          }
        }
      },
      interaction: {
        mode: 'nearest' as const,
        axis: 'x' as const,
        intersect: false
      }    } as any; // 型エラーを回避するためにanyを使用
  }, [metricDataResults]);
  // メトリクスの合計値を計算（グラフと同じ順序）
  const metricSummary = useMemo(() => {
    console.log('[JobMetricDrawer] metricDataResults:', metricDataResults);
    if (!metricDataResults || metricDataResults.length === 0) {
      console.log('[JobMetricDrawer] No metric data available');
      return [];
    }

    // グラフと同じ順序で統計を作成（ソートなし）
    const summary = metricDataResults.map(metric => {
      const total = metric.Values.reduce((sum, val) => sum + val, 0);
      const avg = metric.Values.length > 0 ? total / metric.Values.length : 0;
      const max = metric.Values.length > 0 ? Math.max(...metric.Values) : 0;
      
      return {
        label: metric.Label || 'Unknown',
        total: Math.round(total),
        average: Math.round(avg * 100) / 100,
        max: Math.round(max),
        count: metric.Values.length
      };
    });
    console.log('[JobMetricDrawer] metricSummary:', summary);
    return summary;
  }, [metricDataResults]);

  console.log('[JobMetricDrawer] Render', { loadingDatapoints, metricSummaryLength: metricSummary.length });

  return (
    <>
      {/* 時間間隔設定のラジオボタン */}
      <FormControl mb={4}>
        <RadioGroup
          onChange={(nextValue: "minit" | "5minits" | "hour" | "day") => setPeriodset(nextValue)}
          value={periodset}
        >
          <HStack flexWrap="wrap" spacing="5">
            <Radio value="minit">1分間隔</Radio>
            <Radio value="5minits">5分間隔</Radio>
            <Radio value="hour">１時間間隔</Radio>
            <Radio value="day">１日間隔</Radio>
          </HStack>
        </RadioGroup>
      </FormControl>
      
      {loadingDatapoints ? (
        <Center h="50vh">
          <Spinner color="teal.500" />
        </Center>
      ) : (
        <>
          <Stack bg="white" width={"full"} p="5" className="mixed-chart" h={"50VH"} mb={4}>
            <Line data={chartData} options={chartOptions} />
          </Stack>
          
          {/* メトリクス統計表示 */}
          {metricSummary.length > 0 && (
            <Box bg="gray.50" p={4} borderRadius="md">
              <Text fontSize="lg" fontWeight="bold" mb={3}>メトリック統計</Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                {metricSummary.map((summary, index) => (
                  <Stat key={index} bg="white" p={3} borderRadius="md" boxShadow="sm">
                    <StatLabel fontSize="sm" color="gray.600">{summary.label}</StatLabel>
                    <StatNumber fontSize="2xl" color="teal.600" textAlign="right">{summary.total.toLocaleString()}</StatNumber>
                    <StatHelpText fontSize="xs" textAlign="right">
                      平均: {summary.average.toLocaleString()} | 最大: {summary.max.toLocaleString()}
                    </StatHelpText>
                  </Stat>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </>
      )}
    </>
  );
});
